import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { handleLogout } from '../../controllers/authController';
import { getRequests, getConversations, getMessages, sendMessage as apiSendMessage, adminJoinChat,
         adminCreateUser, adminUpdateUser, getAdminAnalytics, adminGetTaxCertificate,
         adminVerifyTax, getPlatform, updatePlatform } from '../../services/api';
import { encodeHTML } from '../../utils/security';
import { msgStamp } from '../../utils/format';
import { handleGetPaymentAnalytics, handleGetRefunds, handleProcessRefund } from '../../controllers/paymentController';
import API from '../../services/api';

const roleColor   = { customer: 'bg-blue-50 text-blue-700', supplier: 'bg-purple-50 text-purple-700', admin: 'bg-red-50 text-red-700' };
const reqStatusPill = { pending: 'bg-yellow-50 text-yellow-700', quoted: 'bg-blue-50 text-blue-700', completed: 'bg-green-50 text-green-700', cancelled: 'bg-gray-50 text-gray-500' };
const reqStatusLabel = { pending:'Bekliyor', quoted:'Teklif Var', completed:'Tamamlandı', cancelled:'İptal' };

const NAV = [
  { key: 'overview',  icon: '📊', label: 'Genel Bakış'     },
  { key: 'analytics', icon: '📈', label: 'Analitik'        },
  { key: 'revenue',   icon: '💰', label: 'Gelir & Ödemeler'},
  { key: 'users',     icon: '👥', label: 'Kullanıcılar'    },
  { key: 'requests',  icon: '📋', label: 'Talepler'        },
  { key: 'messages',  icon: '✉️', label: 'Mesajlar'        },
  { key: 'suppliers', icon: '🏢', label: 'Tedarikçiler'    },
  { key: 'settings',  icon: '⚙️', label: 'Ayarlar'         },
];

const tl = (v) => `₺${parseFloat(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const REQ_STATUS_LABELS = { pending:'Bekliyor', quoted:'Teklif Var', completed:'Tamamlandı', cancelled:'İptal' };

// Official checks for a vergi levhası: enter the "onay kodu" printed on it.
const GIB_VERIFY_URL    = 'https://dijital.gib.gov.tr/dogrulamalar/ivdBelgeDogrulama';
const EDEVLET_VERIFY_URL = 'https://www.turkiye.gov.tr/gib-intvrg-e-vergi-levhasi-dogrulama';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [nav,       setNav]       = useState('overview');
  const [sideOpen,  setSideOpen]  = useState(false);
  const [users,     setUsers]     = useState([]);
  const [requests,  setRequests]  = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [payData,   setPayData]   = useState(null);   // payment revenue analytics
  const [refunds,   setRefunds]   = useState([]);     // refund requests
  const [loading,   setLoading]   = useState(false);
  const [platform,  setPlatform]  = useState(null);   // { registrations_open, maintenance_mode, maintenance_message }
  const [platMsg,   setPlatMsg]   = useState('');
  const [error,     setError]     = useState('');
  const [feedback,  setFeedback]  = useState('');
  const [search,    setSearch]    = useState('');
  const [roleF,     setRoleF]     = useState('all');

  // ── Chat state ──
  const [convs,      setConvs]      = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages,   setMessages]   = useState([]);
  const [msgText,    setMsgText]    = useState('');

  // ── User create/edit modal ──
  const EMPTY_USER = { role:'customer', email:'', password:'', full_name:'', firm_name:'',
                       phone:'', city:'', category:'', tax_no:'', is_active:true, is_approved:true };
  const [userModal, setUserModal] = useState(null);  // { mode:'create'|'edit', id?, form }
  const setField = (k, v) => setUserModal(m => ({ ...m, form: { ...m.form, [k]: v } }));

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/auth/users/');
      setUsers(res.data);
    } catch {
      // Endpoint may not exist yet — show empty state
      setUsers([]);
    }
    setLoading(false);
  }, []);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequests();
      setRequests(res.data);
    } catch { setError('Talepler yüklenemedi.'); }
    setLoading(false);
  }, []);

  // ── Chat loaders ──
  const loadConvs = useCallback(async () => {
    try {
      const res = await getConversations();
      setConvs(res.data);
      if (res.data.length > 0 && !activeConv) setActiveConv(res.data[0]);
    } catch { setError('Konuşmalar yüklenemedi.'); }
  }, [activeConv]);

  const loadMessages = useCallback(async () => {
    if (!activeConv) return;
    try {
      const res = await getMessages(activeConv.id);
      setMessages(res.data);
    } catch {}
  }, [activeConv]);

  const sendMsg = async () => {
    const clean = msgText.replace(/<[^>]*>/g, '').trim().slice(0, 1000);
    if (!clean || !activeConv) return;
    try {
      const res = await apiSendMessage(activeConv.id, { text: clean });
      setMessages(p => [...p, res.data]);
      setMsgText('');
    } catch { setError('Mesaj gönderilemedi.'); }
  };

  const joinConv = async () => {
    if (!activeConv) return;
    try {
      await adminJoinChat(activeConv.id);
      setActiveConv(c => ({ ...c, admin_joined: true }));
      setConvs(p => p.map(c => c.id === activeConv.id ? { ...c, admin_joined: true } : c));
      setFeedback('Konuşmaya katıldınız. Müşteri ve tedarikçi bilgilendirildi. ✓');
      setTimeout(() => setFeedback(''), 3000);
      loadMessages();
    } catch { setError('Konuşmaya katılınamadı.'); }
  };

  useEffect(() => {
    if (nav === 'overview')   { loadRequests(); loadUsers(); }
    if (nav === 'users')      loadUsers();
    if (nav === 'suppliers')  loadUsers();
    if (nav === 'requests')   loadRequests();
    if (nav === 'messages')   loadConvs();
    if (nav === 'analytics') {
      getAdminAnalytics().then(r => setAnalytics(r.data)).catch(() => setError('Analitik yüklenemedi.'));
    }
    if (nav === 'revenue') loadRevenue();
  }, [nav]);

  const loadRevenue = useCallback(async () => {
    setLoading(true);
    const [a, r] = await Promise.all([handleGetPaymentAnalytics(), handleGetRefunds()]);
    setLoading(false);
    if (a.success) setPayData(a.data); else setError(a.error);
    if (r.success) setRefunds(r.data);
  }, []);

  const resolveRefund = async (id, action) => {
    let note = '';
    if (action === 'reject') { note = window.prompt('Ret gerekçesi (opsiyonel):') || ''; }
    else if (!window.confirm('İadeyi onaylıyor musunuz? Tutar müşteriye iyzico üzerinden geri ödenecek.')) return;
    setError('');
    const res = await handleProcessRefund(id, action, note);
    if (res.success) {
      setFeedback(action === 'approve' ? 'İade onaylandı ve iyzico iadesi yapıldı. ✓' : 'İade talebi reddedildi.');
      loadRevenue();
      setTimeout(() => setFeedback(''), 3500);
    } else setError(res.error);
  };

  useEffect(() => { loadMessages(); }, [activeConv]);

  const filteredUsers = users.filter(u => {
    const ms = !search || u.email?.toLowerCase().includes(search.toLowerCase())
                       || (u.full_name || u.firm_name || '').toLowerCase().includes(search.toLowerCase());
    const mr = roleF === 'all' || u.role === roleF;
    return ms && mr;
  });

  const suppliers = users.filter(u => u.role === 'supplier');
  const pendingSuppliers = suppliers.filter(u => !u.is_approved);

  const approveUser = async (userId) => {
    try {
      await API.patch(`/auth/users/${userId}/approve/`);
      setUsers(p => p.map(u => u.id === userId ? { ...u, is_approved: true, is_active: true } : u));
      setFeedback('Tedarikçi onaylandı! ✓');
      setTimeout(() => setFeedback(''), 3000);
    } catch { setError('İşlem başarısız.'); }
  };

  // A supplier without a vergi levhası can still be approved, but only on purpose.
  const approveSupplier = (s) => {
    if (!s.has_tax_certificate &&
        !window.confirm(`${s.firm_name || s.email} vergi levhası yüklemedi. Yine de onaylamak istiyor musunuz?`)) return;
    approveUser(s.id);
  };

  // The certificate is private (no public URL): fetch it with the admin token,
  // then show it in a new tab. The tab is opened first so popup blockers allow it.
  const openTaxCertificate = async (s) => {
    const tab = window.open('', '_blank');
    try {
      const res = await adminGetTaxCertificate(s.id);
      const url = URL.createObjectURL(res.data);
      if (tab) tab.location.href = url; else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (tab) tab.close();
      setError('Vergi levhası açılamadı.');
    }
  };

  // After checking the certificate (e.g. its onay kodu at GİB), give the supplier
  // the public "Vergi levhası doğrulandı" badge — or take it away again.
  const setTaxVerified = async (s, verified) => {
    if (verified && !window.confirm(
      `${s.firm_name || s.email}: vergi levhasını kontrol ettiniz mi?\n\n` +
      'Onaylarsanız tedarikçi, alıcılara "Vergi levhası doğrulandı" rozetiyle gösterilir.')) return;
    try {
      const res = await adminVerifyTax(s.id, verified);
      setUsers(p => p.map(u => u.id === s.id ? { ...u, ...res.data } : u));
      setFeedback(verified ? 'Vergi levhası doğrulandı olarak işaretlendi. ✓' : 'Doğrulama kaldırıldı.');
      setTimeout(() => setFeedback(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'İşlem başarısız.');
    }
  };

  const blockUser = async (userId) => {
    try {
      await API.patch(`/auth/users/${userId}/block/`);
      setUsers(p => p.map(u => u.id === userId ? { ...u, is_active: false } : u));
    } catch { setError('İşlem başarısız.'); }
  };

  // ── Create / edit / activate-deactivate ──
  const openCreate = () => setUserModal({ mode: 'create', form: { ...EMPTY_USER } });
  const openEdit   = (u) => setUserModal({ mode: 'edit', id: u.id, form: {
    role: u.role, email: u.email, password: '', full_name: u.full_name || '',
    firm_name: u.firm_name || '', phone: u.phone || '', city: u.city || '',
    category: u.category || '', tax_no: u.tax_no || '',
    is_active: u.is_active, is_approved: u.is_approved,
  }});

  const saveUser = async () => {
    const f = userModal.form;
    try {
      if (userModal.mode === 'create') {
        if (!f.email || !f.password) { setError('E-posta ve şifre zorunludur.'); return; }
        const res = await adminCreateUser(f);
        setUsers(p => [res.data, ...p]);
        setFeedback('Kullanıcı oluşturuldu ✓');
      } else {
        const payload = { ...f };
        delete payload.email;                 // email is immutable
        if (!payload.password) delete payload.password;
        const res = await adminUpdateUser(userModal.id, payload);
        setUsers(p => p.map(u => u.id === userModal.id ? res.data : u));
        setFeedback('Kullanıcı güncellendi ✓');
      }
      setUserModal(null);
      setTimeout(() => setFeedback(''), 3000);
    } catch (e) {
      const errs = e.response?.data?.errors;
      setError(errs ? Object.values(errs).flat().join(' ') : 'İşlem başarısız.');
    }
  };

  useEffect(() => {
    if (nav === 'settings') getPlatform().then(r => { setPlatform(r.data); setPlatMsg(r.data.maintenance_message || ''); }).catch(() => {});
  }, [nav]);

  const savePlatform = async (patch) => {
    try { const r = await updatePlatform(patch); setPlatform(r.data); }
    catch { alert('Ayar kaydedilemedi.'); }
  };

  const toggleActive = async (u) => {
    try {
      const res = await adminUpdateUser(u.id, { is_active: !u.is_active });
      setUsers(p => p.map(x => x.id === u.id ? res.data : x));
    } catch (e) {
      setError(e.response?.data?.message || 'İşlem başarısız.');
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      {sideOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setSideOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`w-56 bg-gray-900 flex flex-col fixed top-0 bottom-0 z-40 transition-transform duration-300
        ${sideOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="px-5 py-5 border-b border-white/10">
          <div className="text-white text-lg font-bold">
            Oto<span className="text-red-400">ParçamıBul</span>
          </div>
          <div className="text-red-400/60 text-xs mt-1 font-semibold uppercase tracking-wider">Admin Paneli</div>
        </div>
        <nav className="flex-1 py-3">
          {NAV.map(n => (
            <button key={n.key} onClick={() => { setNav(n.key); setSideOpen(false); }}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition border-l-4
                ${nav === n.key ? 'bg-red-900/30 text-white border-red-500' : 'text-white/50 border-transparent hover:bg-white/5 hover:text-white'}`}>
              <span>{n.icon}</span>{n.label}
              {n.key === 'suppliers' && pendingSuppliers.length > 0 && (
                <span className="ml-auto bg-yellow-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                  {pendingSuppliers.length}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="px-5 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-red-700 text-white text-xs font-bold flex items-center justify-center">A</div>
            <div className="min-w-0">
              <div className="text-white text-xs font-medium">Admin</div>
              <div className="text-white/30 text-xs truncate">{user?.email || 'admin@otoparcamibul.com'}</div>
            </div>
          </div>
          <button onClick={() => handleLogout(logout, navigate)}
            className="w-full text-xs border border-white/20 text-white/50 py-1.5 rounded-lg hover:border-white/40 hover:text-white transition">
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="md:ml-56 flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-gray-200 h-14 flex items-center justify-between px-4 md:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-gray-500 text-xl" onClick={() => setSideOpen(o => !o)}>☰</button>
            <h1 className="text-base font-semibold text-gray-800">{NAV.find(n => n.key === nav)?.label}</h1>
          </div>
          <span className="bg-red-50 text-red-700 border border-red-200 text-xs font-semibold px-3 py-1 rounded-full">🔴 Admin</span>
        </header>

        <div className="p-4 md:p-6 flex-1">
          {feedback && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">{feedback}</div>
          )}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 text-sm flex justify-between">
              <span>{error}</span>
              <button onClick={() => setError('')} className="ml-4 text-red-400">✕</button>
            </div>
          )}

          {/* ── Overview ── */}
          {nav === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Toplam Kullanıcı',   val: users.length },
                  { label: 'Aktif Tedarikçi',    val: suppliers.filter(s => s.is_approved).length },
                  { label: 'Onay Bekleyen',       val: pendingSuppliers.length },
                  { label: 'Toplam Talep',        val: requests.length },
                ].map(s => (
                  <div key={s.label} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">{s.label}</div>
                    <div className="text-2xl font-bold text-gray-900">{s.val}</div>
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                {/* Recent requests */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-gray-800">Son Talepler</h3>
                    <button onClick={() => setNav('requests')} className="text-xs text-blue-700 hover:underline">Tümü →</button>
                  </div>
                  {requests.slice(0, 5).map(r => (
                    <div key={r.id} className="px-5 py-3 border-b border-gray-50 flex justify-between items-center">
                      <div>
                        <p className="text-xs font-semibold text-gray-800">{r.part_name}</p>
                        <p className="text-xs text-gray-400">{r.brand} {r.model} · {new Date(r.created_at).toLocaleDateString('tr-TR')}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${reqStatusPill[r.status]}`}>
                        {reqStatusLabel[r.status]}
                      </span>
                    </div>
                  ))}
                  {requests.length === 0 && !loading && (
                    <p className="px-5 py-6 text-xs text-gray-400 text-center">Henüz talep yok</p>
                  )}
                </div>

                {/* Pending suppliers */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-gray-800">Onay Bekleyen Tedarikçiler</h3>
                    <button onClick={() => setNav('suppliers')} className="text-xs text-blue-700 hover:underline">Tümü →</button>
                  </div>
                  {pendingSuppliers.slice(0, 5).map(s => (
                    <div key={s.id} className="px-5 py-3 border-b border-gray-50 flex justify-between items-center">
                      <div>
                        <p className="text-xs font-semibold text-gray-800">{s.firm_name || s.email}</p>
                        <p className="text-xs text-gray-400">{s.city} · {s.email}</p>
                        {s.has_tax_certificate ? (
                          <button onClick={() => openTaxCertificate(s)} className="text-xs text-blue-600 hover:underline">📄 Vergi levhası</button>
                        ) : (
                          <span className="text-xs text-amber-600">⚠️ Vergi levhası yok</span>
                        )}
                      </div>
                      <button onClick={() => approveSupplier(s)}
                        className="text-xs bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-lg hover:bg-green-100 transition">
                        Onayla
                      </button>
                    </div>
                  ))}
                  {pendingSuppliers.length === 0 && (
                    <p className="px-5 py-6 text-xs text-gray-400 text-center">Onay bekleyen tedarikçi yok ✓</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Analytics ── */}
          {nav === 'analytics' && (
            <div className="space-y-6">
              {!analytics && <p className="text-center text-gray-400 py-10 text-sm">Yükleniyor...</p>}
              {analytics && (() => {
                const t = analytics.totals;
                const maxDay = Math.max(1, ...analytics.requests_per_day.map(d => d.count));
                const maxCat = Math.max(1, ...analytics.top_categories.map(c => c.count));
                const statusOrder = ['pending', 'quoted', 'completed', 'cancelled'];
                return (
                  <>
                    {/* Totals */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                      {[
                        ['Toplam Talep', t.requests],
                        ['Toplam Teklif', t.quotes],
                        ['Kabul Edilen', t.accepted_quotes],
                        ['Dönüşüm', `%${t.conversion}`],
                        ['Tedarikçi', t.suppliers],
                        ['Müşteri', t.customers],
                      ].map(([lbl, val]) => (
                        <div key={lbl} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">{lbl}</div>
                          <div className="text-2xl font-bold text-gray-800">{val}</div>
                        </div>
                      ))}
                    </div>

                    {/* Requests per day (last 30 days) */}
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                      <h3 className="text-sm font-bold text-gray-800 mb-4">Son 30 Gün — Günlük Talep</h3>
                      {analytics.requests_per_day.length === 0 ? (
                        <p className="text-xs text-gray-400">Bu dönemde talep yok.</p>
                      ) : (
                        <div className="flex items-end gap-1 h-40">
                          {analytics.requests_per_day.map(d => (
                            <div key={d.day} className="flex-1 flex flex-col items-center justify-end group" title={`${d.day}: ${d.count}`}>
                              <div className="w-full bg-blue-600 rounded-t hover:bg-blue-700 transition"
                                   style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: '2px' }} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* By status */}
                      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                        <h3 className="text-sm font-bold text-gray-800 mb-4">Talep Durumları</h3>
                        <div className="space-y-2">
                          {statusOrder.map(s => {
                            const c = analytics.by_status[s] || 0;
                            const pct = t.requests ? (c / t.requests) * 100 : 0;
                            return (
                              <div key={s} className="flex items-center gap-3">
                                <span className="w-24 text-xs text-gray-500">{REQ_STATUS_LABELS[s]}</span>
                                <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                                  <div className="bg-blue-600 h-3 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="w-8 text-right text-xs font-semibold text-gray-700">{c}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Top categories */}
                      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                        <h3 className="text-sm font-bold text-gray-800 mb-4">En Çok Talep Edilen Kategoriler</h3>
                        <div className="space-y-2">
                          {analytics.top_categories.length === 0 && <p className="text-xs text-gray-400">Veri yok.</p>}
                          {analytics.top_categories.map(c => (
                            <div key={c.category} className="flex items-center gap-3">
                              <span className="w-32 text-xs text-gray-500 truncate" title={c.category}>{c.category || '—'}</span>
                              <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                                <div className="bg-green-600 h-3 rounded-full" style={{ width: `${(c.count / maxCat) * 100}%` }} />
                              </div>
                              <span className="w-8 text-right text-xs font-semibold text-gray-700">{c.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* ── Revenue & payments ── */}
          {nav === 'revenue' && (
            <div>
              {loading && !payData && <p className="text-center text-gray-400 py-10 text-sm">Yükleniyor...</p>}

              {/* Totals */}
              {payData && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                  {[
                    { label: 'Toplam Ciro',      val: tl(payData.totals.gross),      sub: `${payData.totals.count} sipariş`, color: 'text-gray-900' },
                    { label: 'Komisyon (Bizim)', val: tl(payData.totals.commission), sub: 'Platform geliri',                color: 'text-green-700' },
                    { label: 'Tedarikçi Payı',   val: tl(payData.totals.payout),     sub: 'Tedarikçilere',                  color: 'text-gray-900' },
                    { label: 'Sipariş Sayısı',   val: payData.totals.count,          sub: 'Ödenmiş',                        color: 'text-gray-900' },
                  ].map(s => (
                    <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                      <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">{s.label}</div>
                      <div className={`text-xl font-bold ${s.color}`}>{s.val}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{s.sub}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Daily breakdown */}
              {payData && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
                  <div className="px-5 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-bold text-gray-800">Günlük Ciro & Komisyon</h3>
                  </div>
                  {payData.daily.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Henüz ödeme yok.</p>
                  ) : (
                    <div className="divide-y divide-gray-50">
                      <div className="hidden sm:grid grid-cols-4 gap-4 px-5 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase">
                        <div>Tarih</div><div className="text-right">Ciro</div><div className="text-right">Komisyon</div><div className="text-right">Sipariş</div>
                      </div>
                      {payData.daily.map(d => (
                        <div key={d.day} className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 px-5 py-2.5 text-sm">
                          <div className="text-gray-700">{new Date(d.day).toLocaleDateString('tr-TR')}</div>
                          <div className="sm:text-right text-gray-800 font-medium">{tl(d.gross)}</div>
                          <div className="sm:text-right text-green-700 font-medium">{tl(d.commission)}</div>
                          <div className="sm:text-right text-gray-500">{d.count}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Refund requests */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-800">İade Talepleri</h3>
                  {refunds.filter(r => r.status === 'requested').length > 0 && (
                    <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                      {refunds.filter(r => r.status === 'requested').length} bekliyor
                    </span>
                  )}
                </div>
                {refunds.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">İade talebi yok.</p>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {refunds.map(r => (
                      <div key={r.id} className="px-5 py-4 flex flex-col md:flex-row md:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="text-sm font-semibold text-gray-800">İade #{r.id}</span>
                            <span className="text-xs text-gray-400">{r.buyer_name} → {r.supplier_name}</span>
                            <span className={`text-xs border px-2 py-0.5 rounded-full ${
                              r.status === 'requested' ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                              : r.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                              {r.status === 'requested' ? 'Bekliyor' : r.status === 'approved' ? 'Onaylandı' : 'Reddedildi'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">{r.part_info} · {tl(r.amount)}</p>
                          <p className="text-xs text-gray-600 mt-1 italic">"{r.reason}"</p>
                        </div>
                        {r.status === 'requested' && (
                          <div className="flex gap-2 flex-shrink-0">
                            <button onClick={() => resolveRefund(r.id, 'reject')}
                              className="border border-gray-200 text-gray-500 text-xs px-3 py-2 rounded-lg hover:border-red-300 hover:text-red-600 transition">
                              Reddet
                            </button>
                            <button onClick={() => resolveRefund(r.id, 'approve')}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition">
                              Onayla & İade Et
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent payments */}
              {payData && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-bold text-gray-800">Son Ödemeler</h3>
                  </div>
                  {payData.recent.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Henüz ödeme yok.</p>
                  ) : (
                    <div className="divide-y divide-gray-50">
                      {payData.recent.map(o => (
                        <div key={o.id} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-800">
                              <span className="font-semibold">{o.buyer_name}</span>
                              <span className="text-gray-400"> → </span>
                              <span className="font-semibold">{o.supplier_name}</span>
                            </div>
                            <p className="text-xs text-gray-400 truncate">{o.part_info}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-sm font-bold text-gray-900">{tl(o.paid_price || o.price)}</div>
                            <div className="text-xs text-green-700">komisyon {tl(o.commission_amount)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Users ── */}
          {nav === 'users' && (
            <div>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-5 flex flex-wrap gap-3">
                <input type="text"
                  className="flex-1 min-w-40 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600"
                  placeholder="🔍 İsim veya e-posta ara..."
                  value={search}
                  onChange={e => setSearch(e.target.value.replace(/<[^>]*>/g, ''))}
                />
                <select className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none"
                  value={roleF} onChange={e => setRoleF(e.target.value)}>
                  <option value="all">Tüm Roller</option>
                  <option value="customer">Müşteri</option>
                  <option value="supplier">Tedarikçi</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="text-xs text-gray-400 self-center">{filteredUsers.length} kullanıcı</p>
                <button onClick={openCreate}
                  className="ml-auto bg-red-700 hover:bg-red-800 text-white text-xs font-semibold px-4 py-2 rounded-lg transition">
                  + Yeni Kullanıcı
                </button>
              </div>

              {loading && <p className="text-center text-gray-400 py-10 text-sm">Yükleniyor...</p>}

              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <div className="col-span-3">Kullanıcı</div>
                  <div className="col-span-3">E-posta</div>
                  <div className="col-span-1">Rol</div>
                  <div className="col-span-2">Şehir</div>
                  <div className="col-span-1">Durum</div>
                  <div className="col-span-2">İşlem</div>
                </div>
                {filteredUsers.length === 0 && !loading && (
                  <p className="text-center text-gray-400 py-8 text-sm">Kullanıcı bulunamadı</p>
                )}
                {filteredUsers.map(u => (
                  <div key={u.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 px-4 md:px-5 py-3.5 border-b border-gray-50 hover:bg-gray-50 transition items-center">
                    <div className="col-span-3 flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-blue-700 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {(u.full_name || u.firm_name || u.email)?.[0]?.toUpperCase()}
                      </div>
                      <span className="text-sm text-gray-800 font-medium truncate">
                        {u.full_name || u.firm_name || '—'}
                      </span>
                    </div>
                    <div className="col-span-3 text-xs text-gray-500 truncate">{u.email}</div>
                    <div className="col-span-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${roleColor[u.role]}`}>
                        {u.role === 'customer' ? 'Müşteri' : u.role === 'supplier' ? 'Tedarikçi' : 'Admin'}
                      </span>
                    </div>
                    <div className="col-span-2 text-xs text-gray-500">{u.city || '—'}</div>
                    <div className="col-span-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full
                        ${u.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {u.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                    <div className="col-span-2 flex gap-1.5 flex-wrap">
                      <button onClick={() => openEdit(u)}
                        className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-lg hover:bg-blue-100 transition">
                        Düzenle
                      </button>
                      {u.role === 'supplier' && !u.is_approved && (
                        <button onClick={() => approveUser(u.id)}
                          className="text-xs bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-lg hover:bg-green-100 transition">
                          Onayla
                        </button>
                      )}
                      {u.role !== 'admin' && (
                        u.is_active ? (
                          <button onClick={() => toggleActive(u)}
                            className="text-xs bg-red-50 text-red-700 border border-red-200 px-2.5 py-1 rounded-lg hover:bg-red-100 transition">
                            Pasifleştir
                          </button>
                        ) : (
                          <button onClick={() => toggleActive(u)}
                            className="text-xs bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-lg hover:bg-green-100 transition">
                            Aktifleştir
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Requests ── */}
          {nav === 'requests' && (
            <div>
              {loading && <p className="text-center text-gray-400 py-10 text-sm">Yükleniyor...</p>}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <div className="col-span-1">No</div>
                  <div className="col-span-3">Parça</div>
                  <div className="col-span-3">Araç</div>
                  <div className="col-span-2">Tarih</div>
                  <div className="col-span-1">Teklif</div>
                  <div className="col-span-2">Durum</div>
                </div>
                {requests.length === 0 && !loading && (
                  <p className="text-center text-gray-400 py-8 text-sm">Talep bulunamadı</p>
                )}
                {requests.map(r => (
                  <div key={r.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 px-4 md:px-5 py-3.5 border-b border-gray-50 hover:bg-gray-50 transition items-center">
                    <div className="col-span-1 text-xs text-gray-400 font-mono">#{r.id}</div>
                    <div className="col-span-3 text-sm font-semibold text-gray-800">{r.part_name}</div>
                    <div className="col-span-3 text-xs text-gray-500">{r.brand} {r.model} {r.year}</div>
                    <div className="col-span-2 text-xs text-gray-400">{new Date(r.created_at).toLocaleDateString('tr-TR')}</div>
                    <div className="col-span-1 text-xs text-gray-500">{r.quote_count || 0}</div>
                    <div className="col-span-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${reqStatusPill[r.status]}`}>
                        {reqStatusLabel[r.status]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Suppliers ── */}
          {nav === 'suppliers' && (
            <div>
              {pendingSuppliers.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-5 py-4 mb-5">
                  <p className="text-sm font-semibold text-yellow-800 mb-1">⏳ {pendingSuppliers.length} tedarikçi onay bekliyor</p>
                  <p className="text-xs text-yellow-700">Tedarikçi hesaplarını onaylamak için aşağıdaki listeyi inceleyin.</p>
                </div>
              )}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <div className="col-span-3">Firma</div>
                  <div className="col-span-3">E-posta</div>
                  <div className="col-span-2">Şehir</div>
                  <div className="col-span-2">Durum</div>
                  <div className="col-span-2">İşlem</div>
                </div>
                {suppliers.length === 0 && !loading && (
                  <p className="text-center text-gray-400 py-8 text-sm">Tedarikçi bulunamadı</p>
                )}
                {suppliers.map(s => (
                  <div key={s.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 px-4 md:px-5 py-3.5 border-b border-gray-50 hover:bg-gray-50 transition items-center">
                    <div className="col-span-3">
                      <div className="text-sm font-semibold text-gray-800">{s.firm_name || '—'}</div>
                      {s.tax_no && (
                        <a href={`https://www.google.com/search?q=${encodeURIComponent(`"${s.firm_name || ''}" ${s.tax_no}`)}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline" title="Firma adı + numarayı web'de ara">
                          {s.tax_no.length === 11 ? 'TCKN' : 'VKN'}: {s.tax_no} 🔍
                        </a>
                      )}
                      <div className="flex flex-wrap items-center gap-x-2 mt-0.5">
                        {s.has_tax_certificate ? (
                          <>
                            <button onClick={() => openTaxCertificate(s)} className="text-xs text-blue-600 hover:underline">📄 Vergi levhası</button>
                            <a href={GIB_VERIFY_URL} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-gray-500 hover:text-blue-600 hover:underline"
                              title="Levhadaki onay kodu ile GİB'de doğrulayın">GİB'de doğrula ↗</a>
                            <a href={EDEVLET_VERIFY_URL} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-gray-500 hover:text-blue-600 hover:underline">e-Devlet ↗</a>
                            {s.tax_verified ? (
                              <button onClick={() => setTaxVerified(s, false)} title="Doğrulamayı kaldır"
                                className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-1.5 rounded">
                                ✓ Doğrulandı
                              </button>
                            ) : (
                              <button onClick={() => setTaxVerified(s, true)}
                                className="text-xs font-semibold text-blue-700 hover:underline">
                                Doğrulandı olarak işaretle
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-amber-600">⚠️ Vergi levhası yok</span>
                        )}
                      </div>
                    </div>
                    <div className="col-span-3 text-xs text-gray-500 truncate">{s.email}</div>
                    <div className="col-span-2 text-xs text-gray-500">{s.city || '—'}</div>
                    <div className="col-span-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full
                        ${s.is_approved ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                        {s.is_approved ? '✓ Onaylı' : '⏳ Bekliyor'}
                      </span>
                    </div>
                    <div className="col-span-2 flex gap-1.5">
                      {!s.is_approved && (
                        <button onClick={() => approveSupplier(s)}
                          className="text-xs bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-lg hover:bg-green-100 transition">
                          Onayla
                        </button>
                      )}
                      {s.is_active && (
                        <button onClick={() => blockUser(s.id)}
                          className="text-xs bg-red-50 text-red-700 border border-red-200 px-2.5 py-1 rounded-lg hover:bg-red-100 transition">
                          Engelle
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Messages (admin oversight + intervention) ── */}
          {nav === 'messages' && (
            <div className="flex flex-col md:flex-row gap-4 h-[72vh]">
              {/* Conversation list */}
              <div className="w-full md:w-64 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-y-auto flex-shrink-0">
                <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tüm Konuşmalar</p>
                  <button onClick={loadConvs} className="text-xs text-blue-700 hover:underline">Yenile</button>
                </div>
                {convs.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-6">Henüz konuşma yok</p>
                )}
                {convs.map(c => (
                  <button key={c.id} onClick={() => setActiveConv(c)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition
                      ${activeConv?.id === c.id ? 'bg-red-50' : ''}`}>
                    <p className="text-xs font-semibold text-gray-800 truncate">
                      {c.participants?.customer || 'Müşteri'} <span className="text-gray-300">↔</span> {c.participants?.supplier || 'Tedarikçi'}
                    </p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{c.request_summary}</p>
                    {c.admin_joined && (
                      <span className="mt-1 inline-block bg-red-50 text-red-600 text-xs px-1.5 py-0.5 rounded-full">Katıldınız</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Chat window */}
              <div className="flex-1 bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col overflow-hidden">
                {!activeConv ? (
                  <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                    Bir konuşma seçin
                  </div>
                ) : (
                  <>
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex justify-between items-center gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {activeConv.participants?.customer} ↔ {activeConv.participants?.supplier}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{activeConv.request_summary}</p>
                      </div>
                      {!activeConv.admin_joined && (
                        <button onClick={joinConv}
                          className="flex-shrink-0 text-xs bg-red-700 hover:bg-red-800 text-white font-semibold px-3 py-1.5 rounded-lg transition">
                          Konuşmaya Katıl
                        </button>
                      )}
                    </div>
                    <div className="flex-1 overflow-y-auto p-5 space-y-3">
                      {messages.map(m => (
                        <div key={m.id} className={`flex ${m.is_mine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-xs md:max-w-md px-4 py-2.5 rounded-2xl text-sm
                            ${m.is_mine
                              ? 'bg-red-700 text-white rounded-br-none'
                              : m.sender_role === 'supplier'
                                ? 'bg-purple-50 text-gray-800 rounded-bl-none'
                                : 'bg-gray-100 text-gray-800 rounded-bl-none'}`}>
                            {!m.is_mine && (
                              <p className="text-xs font-semibold mb-0.5 opacity-70">
                                {m.sender_name} · {m.sender_role === 'supplier' ? 'Tedarikçi' : m.sender_role === 'admin' ? 'Admin' : 'Müşteri'}
                              </p>
                            )}
                            <p dangerouslySetInnerHTML={{ __html: encodeHTML(m.text) }} />
                            <p className={`text-xs mt-1 ${m.is_mine ? 'text-red-200' : 'text-gray-400'}`}>
                              {msgStamp(m.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                      {messages.length === 0 && (
                        <p className="text-center text-gray-300 text-xs py-8">Bu konuşmada henüz mesaj yok</p>
                      )}
                    </div>
                    <div className="p-4 border-t border-gray-100 flex gap-3">
                      <input type="text"
                        className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-600 transition"
                        placeholder="Destek mesajınızı yazın..."
                        value={msgText}
                        maxLength={1000}
                        onChange={e => setMsgText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && sendMsg()}
                      />
                      <button onClick={sendMsg}
                        className="bg-red-700 hover:bg-red-800 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition">
                        Gönder
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Settings ── */}
          {nav === 'settings' && (
            <div className="max-w-lg space-y-4">
              {!platform ? <p className="text-sm text-gray-400">Yükleniyor…</p> : (
                <>
                  {[
                    { key: 'registrations_open', label: 'Yeni Kayıtlar', desc: 'Kapalıyken müşteri ve tedarikçi kaydı alınmaz.', on: platform.registrations_open },
                    { key: 'maintenance_mode',   label: 'Site Bakım Modu', desc: 'Açıkken yönetici dışındaki herkes için site (web ve uygulama) kapanır.', on: platform.maintenance_mode },
                  ].map(s => (
                    <div key={s.key} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center justify-between">
                      <div className="pr-4">
                        <p className="text-sm font-semibold text-gray-800">{s.label}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{s.desc}</p>
                      </div>
                      <button type="button" role="switch" aria-checked={s.on} aria-label={s.label}
                        onClick={() => {
                          if (s.key === 'maintenance_mode' && !s.on && !window.confirm('Bakım modu açılsın mı? Yönetici dışındaki herkes siteye erişemez.')) return;
                          savePlatform({ [s.key]: !s.on });
                        }}
                        className={`w-11 h-6 shrink-0 rounded-full flex items-center transition-colors ${s.on ? 'bg-blue-700' : 'bg-gray-200'}`}>
                        <span className={`w-4 h-4 bg-white rounded-full shadow mx-1 transition-transform ${s.on ? 'translate-x-5' : ''}`} />
                      </button>
                    </div>
                  ))}
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <label className="text-xs font-semibold text-gray-500">Bakım mesajı</label>
                    <input type="text" maxLength={200} value={platMsg} onChange={e => setPlatMsg(e.target.value)}
                      placeholder="Site bakımda. Kısa süre sonra tekrar deneyin."
                      className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-600" />
                    <button onClick={() => savePlatform({ maintenance_message: platMsg })}
                      className="mt-3 px-4 py-2 bg-blue-700 text-white text-xs font-semibold rounded-lg hover:bg-blue-800">Kaydet</button>
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </main>

      {/* ── User create / edit modal ── */}
      {userModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setUserModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-sm font-bold text-gray-800">
                {userModal.mode === 'create' ? 'Yeni Kullanıcı Oluştur' : 'Kullanıcıyı Düzenle'}
              </h3>
              <button onClick={() => setUserModal(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-500">Rol</label>
                <select value={userModal.form.role} onChange={e => setField('role', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-600">
                  <option value="customer">Müşteri</option>
                  <option value="supplier">Tedarikçi</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500">E-posta</label>
                <input type="email" value={userModal.form.email}
                  disabled={userModal.mode === 'edit'}
                  onChange={e => setField('email', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-600 disabled:bg-gray-50 disabled:text-gray-400" />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500">
                  Şifre {userModal.mode === 'edit' && <span className="text-gray-300 font-normal">(boş bırakın = değişmez)</span>}
                </label>
                <input type="password" value={userModal.form.password}
                  onChange={e => setField('password', e.target.value)}
                  placeholder={userModal.mode === 'create' ? 'En az 8 karakter' : '••••••••'}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-600" />
              </div>

              {userModal.form.role === 'supplier' ? (
                <>
                  <div>
                    <label className="text-xs font-semibold text-gray-500">Firma Adı</label>
                    <input type="text" value={userModal.form.firm_name} onChange={e => setField('firm_name', e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-600" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500">Vergi No</label>
                      <input type="text" value={userModal.form.tax_no} onChange={e => setField('tax_no', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-600" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">Kategori</label>
                      <input type="text" value={userModal.form.category} onChange={e => setField('category', e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-600" />
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <label className="text-xs font-semibold text-gray-500">Ad Soyad</label>
                  <input type="text" value={userModal.form.full_name} onChange={e => setField('full_name', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-600" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500">Telefon</label>
                  <input type="text" value={userModal.form.phone} onChange={e => setField('phone', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-600" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">Şehir</label>
                  <input type="text" value={userModal.form.city} onChange={e => setField('city', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-600" />
                </div>
              </div>

              <div className="flex flex-wrap gap-4 pt-1">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={userModal.form.is_active}
                    onChange={e => setField('is_active', e.target.checked)} />
                  Aktif
                </label>
                {userModal.form.role === 'supplier' && (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={userModal.form.is_approved}
                      onChange={e => setField('is_approved', e.target.checked)} />
                    Onaylı Tedarikçi
                  </label>
                )}
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-100 flex gap-2 justify-end sticky bottom-0 bg-white">
              <button onClick={() => setUserModal(null)}
                className="text-sm border border-gray-200 text-gray-500 px-4 py-2 rounded-lg hover:bg-gray-50 transition">
                İptal
              </button>
              <button onClick={saveUser}
                className="text-sm bg-red-700 hover:bg-red-800 text-white font-semibold px-5 py-2 rounded-lg transition">
                {userModal.mode === 'create' ? 'Oluştur' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
